import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Params } from '@angular/router';
import { Location } from '@angular/common';

import { distinctUntilChanged, map, switchMap, take, tap, withLatestFrom } from 'rxjs/operators';
import { BehaviorSubject, combineLatest, Observable, Subscription } from 'rxjs';
import { Store } from '@ngrx/store';

import { LanguageService } from '@fcom/ui-translate';
import { LocalDate, unsubscribe } from '@fcom/core/utils';
import { LocationRouteCffService } from '@fcom/core/services';
import { AppState, GlobalBookingTravelClass, SentryLogger } from '@fcom/core';
import { finShare } from '@fcom/rx';
import { getTripTypeFromBookingLocationCodes } from '@fcom/common-booking/utils/common-booking.utils';
import { GtmService } from '@fcom/common/gtm';
import { Cabin } from '@fcom/dapi/api/models';
import { DataCloudService } from '@fcom/common/datacloud';
import { FinnairMultiCitySearchInterface, OfferListFetchParams } from '@fcom/common/interfaces';
import { BookingLocationCodes, SelectionPhase } from '@fcom/common/interfaces/booking';
import { WidgetTab } from '@fcom/booking-widget/interfaces';
import { loginStatus } from '@fcom/core/selectors';
import { LoginStatus } from '@fcom/core-api/login';

import { BookingService } from '../../../services';
import {
  BookingAirBoundsService,
  BookingMultiCityService,
  BookingOfferListService,
  PreSelectionParams,
  TicketSelectionParams,
} from '../services';

const getPreSelectionParams = (p: Params): PreSelectionParams => {
  const selectedFlightsAndFareFamily: PreSelectionParams = {
    departureFlightNumbers: '',
    returnFlightNumbers: '',
    departureFareFamily: null,
    returnFareFamily: null,
  };
  if (p.flights?.[0]) {
    selectedFlightsAndFareFamily.departureFlightNumbers = p.flights[0].flightNumbers?.join(',');
    selectedFlightsAndFareFamily.departureFareFamily = p.flights[0].fareFamily;
  }

  if (p.flights?.[1]) {
    selectedFlightsAndFareFamily.returnFlightNumbers = p.flights[1].flightNumbers?.join(',');
    selectedFlightsAndFareFamily.returnFareFamily = p.flights[1].fareFamily;
  }

  return selectedFlightsAndFareFamily;
};

const paramsAndLocaleToOfferListFetchParams = (
  queryParams: Params,
  locale: string,
  sentryLogger: SentryLogger
): TicketSelectionParams => {
  let params: FinnairMultiCitySearchInterface;

  try {
    params = JSON.parse(decodeURIComponent(queryParams.json));
  } catch (error) {
    sentryLogger.error('Error parsing json query params', { error: error.message, json: queryParams.json });
  }
  const {
    cabin = Cabin.MIXED,
    flights = [],
    adults = 0,
    children = 0,
    infants = 0,
    c15s = 0,
    directFlights = false,
    isAward = false,
    promoCode,
  } = params ?? {};

  return {
    locale,
    flights: flights.map((flight) => {
      return {
        ...flight,
        departureDate: new LocalDate(flight.departureDate),
      };
    }),
    cabin: cabin === Cabin.FIRST ? Cabin.BUSINESS : Cabin[cabin],
    paxAmount: {
      adults,
      children,
      infants,
      c15s,
    },
    directFlights,
    ...getPreSelectionParams(params),
    isAward,
    promoCode,
  };
};

@Component({
  selector: 'fin-booking-ticket-selection-route',
  templateUrl: './booking-ticket-selection-route.component.html',
})
export class BookingTicketSelectionRouteComponent implements OnInit, OnDestroy {
  fetchParams$: Observable<OfferListFetchParams>;
  useFpp$: Observable<boolean>;
  isMultiCity$: Observable<boolean>;
  selectionPhase: SelectionPhase;
  goToOutboundPhase$: BehaviorSubject<void> = new BehaviorSubject(undefined);

  private subscriptions = new Subscription();

  constructor(
    private languageService: LanguageService,
    private bookingService: BookingService,
    private bookingOfferListService: BookingOfferListService,
    private bookingAirBoundsService: BookingAirBoundsService,
    private locationRouteCffService: LocationRouteCffService,
    private activatedRoute: ActivatedRoute,
    private gtmService: GtmService,
    private dataCloudService: DataCloudService,
    private bookingMultiCityService: BookingMultiCityService,
    private store$: Store<AppState>,
    private sentryLogger: SentryLogger,
    private location: Location
  ) {}

  ngOnInit(): void {
    this.location.subscribe(() => {
      if (this.selectionPhase === SelectionPhase.INBOUND) {
        this.goToOutboundPhase$.next();
      }
    });

    this.fetchParams$ = this.activatedRoute.queryParams.pipe(
      distinctUntilChanged(),
      withLatestFrom(this.languageService.locale),
      map(([params, locale]) => paramsAndLocaleToOfferListFetchParams(params, locale, this.sentryLogger)),
      finShare()
    );

    this.useFpp$ = this.activatedRoute.queryParams.pipe(
      switchMap((p) => {
        const params = p.json ? JSON.parse(decodeURIComponent(p.json)) : p;
        const origin = params.origin || params.flights[0].origin;
        const destination = params.destination || params.flights[0].destination;
        return this.locationRouteCffService
          .routeCffsFor(origin, destination, params.isAward, this.languageService.localeValue)
          .pipe(
            take(1),
            tap((locationRouteCffData) => {
              this.bookingService.setRouteType(locationRouteCffData.routeType);
            }),
            map((locationRouteCffData) => !!locationRouteCffData.fpp)
          );
      }),
      finShare()
    );

    this.subscriptions.add(
      this.fetchParams$.subscribe((p) => {
        this.updateBookingSearchParameters(p);
        this.dataCloudService.flightSearch(p);
      })
    );

    this.subscriptions.add(
      this.fetchParams$
        .pipe(
          distinctUntilChanged(),
          switchMap((p) =>
            this.bookingService.getLocationsFromFlights(p.flights).pipe(
              map((locations) => ({
                params: p,
                locations,
              }))
            )
          )
        )
        .subscribe(({ params, locations }) => this.gtmService.flightSearch(params, locations))
    );

    this.isMultiCity$ = this.fetchParams$.pipe(
      map((params) => {
        return params.flights.length > 2;
      })
    );

    this.fetchOffers();
  }

  ngOnDestroy(): void {
    unsubscribe(this.subscriptions);
  }

  fetchOffers(): void {
    this.subscriptions.add(
      combineLatest([
        this.fetchParams$,
        this.useFpp$,
        this.store$.pipe(
          loginStatus(),
          map((status) => status === LoginStatus.LOGGED_IN || status === LoginStatus.NOT_LOGGED_IN),
          distinctUntilChanged(),
          finShare()
        ),
      ])
        .pipe(distinctUntilChanged())
        .subscribe(([params, useFpp, isLoginStatusChanged]) => {
          // Fetch multicity offers if this is a multi-city search.
          if (params.flights.length > 2) {
            this.bookingMultiCityService.fetchMultiCityOffers(params);
            return;
          }
          if (useFpp) {
            this.bookingAirBoundsService.triggerFetchBounds(params, isLoginStatusChanged);
          } else {
            this.bookingOfferListService.triggerFetchOffers(params);
          }
        })
    );
  }

  /**
   * Updates the search parameters for previous steps of the flow to allow deep-linked
   * flows to access previous steps
   * @param {OfferListFetchParams} p
   */
  private updateBookingSearchParameters(p: OfferListFetchParams) {
    const locations: BookingLocationCodes[] = p.flights.map((ond) => ({
      originLocationCode: ond.origin,
      destinationLocationCode: ond.destination,
    }));
    this.bookingService.setTripType(getTripTypeFromBookingLocationCodes(locations));
    this.bookingService.setFlightsFromLocationsCodes(p.flights);
    this.bookingService.setPaxAmount(p.paxAmount);
    this.bookingService.selectTravelClass(GlobalBookingTravelClass[p.cabin]);
    this.bookingService.setActiveTab(p.isAward ? WidgetTab.AWARD : WidgetTab.FLIGHT);
  }

  selectionPhaseChanged(phase: SelectionPhase): void {
    if (phase === SelectionPhase.INBOUND) {
      // Add current phase to the browser history stack so that
      // when the user navigates back from the inbound phase
      // the browser will stay in flight selection
      this.location.go(this.location.path());
    }
    this.selectionPhase = phase;
  }
}
